import { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../api/axios';

const C = {
  green: '#3cb74e', black: '#111111', white: '#ffffff',
  red: '#ef4444', amber: '#f59e0b', gray: '#64748b', border: '#e2e8f0',
};
const PIE_COLORS = ['#3cb74e','#111111','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#f97316','#84cc16'];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: C.white, border: `1.5px solid ${C.border}`, borderRadius: 10, padding: '8px 12px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
      <p style={{ color: C.black, fontWeight: 700, fontSize: 12, margin: '0 0 2px' }}>{payload[0].name}</p>
      <p style={{ color: C.green, fontSize: 12, margin: 0, fontWeight: 600 }}>{payload[0].value} emails</p>
    </div>
  );
};

const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, name }) => {
  if (percent < 0.06) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#ffffff" textAnchor="middle" dominantBaseline="central" fontSize={10} fontWeight={700}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

export default function DomainBreakdown() {
  const [data, setData]           = useState([]);
  const [topInvalid, setTopInvalid] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/email/stats/domains').catch(() => ({ data: { data: [] } })),
      api.get('/email/stats/top-invalid').catch(() => ({ data: { data: [] } })),
    ]).then(([d, t]) => {
      setData(d.data.data || []);
      setTopInvalid(t.data.data || []);
      setLoading(false);
    });
  }, []);

  const pieData = data.map(d => ({ name: d.domain, value: parseInt(d.total) }));
  const maxInvalid = topInvalid[0]?.invalid_count || 1;

  if (loading) return (
    <div style={{ ...s.grid }}>
      {[0, 1].map(i => (
        <div key={i} style={{ ...s.card, display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200 }}>
          <p style={{ color: C.gray, fontSize: 13 }}>Loading...</p>
        </div>
      ))}
    </div>
  );

  return (
    <div style={s.grid}>
      {/* Pie chart */}
      <div style={s.card}>
        <h3 style={s.title}>🌐 Domain Breakdown</h3>
        {pieData.length === 0 ? (
          <div style={s.empty}><p style={{ fontSize: 28 }}>🌐</p><p>No domain data yet</p></div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={210}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" outerRadius={85} dataKey="value" labelContent={<CustomLabel />}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
            {/* Legend */}
            <div style={s.pieLegend}>
              {pieData.slice(0, 6).map((d, i) => (
                <div key={i} style={s.legendItem}>
                  <span style={{ ...s.legendDot, background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span style={s.legendLabel} title={d.name}>{d.name}</span>
                  <span style={s.legendValue}>{d.value}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Top Invalid Domains */}
      <div style={s.card}>
        <h3 style={s.title}>❌ Top Invalid Domains</h3>
        {topInvalid.length === 0 ? (
          <div style={s.empty}><p style={{ fontSize: 28 }}>✅</p><p>No invalid domains yet — great list quality!</p></div>
        ) : (
          <div style={s.domainList}>
            {topInvalid.map((d, i) => {
              const barPct = Math.min((d.invalid_count / maxInvalid) * 100, 100);
              const isBad  = d.invalid_pct >= 50;
              return (
                <div key={i} style={s.domainRow}>
                  <span style={{ ...s.domainRank, background: isBad ? '#fee2e2' : '#f1f5f9', color: isBad ? C.red : C.gray }}>
                    {i + 1}
                  </span>
                  <div style={s.domainInfo}>
                    <div style={s.domainTop}>
                      <span style={s.domainName}>{d.domain}</span>
                      <span style={{ ...s.domainPct, color: isBad ? C.red : C.amber }}>
                        {isBad ? '🚨' : '⚠️'} {d.invalid_pct}% invalid
                      </span>
                    </div>
                    <div style={s.barTrack}>
                      <div style={{ ...s.barFill, width: `${barPct}%`, background: isBad ? C.red : C.amber }} />
                    </div>
                    <p style={s.domainSub}>{d.invalid_count} invalid of {d.total} total</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const s = {
  grid:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  card:        { background: '#ffffff', borderRadius: 14, border: '1.5px solid #e2e8f0', padding: 20, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  title:       { color: '#111111', fontSize: 15, fontWeight: 700, margin: '0 0 14px' },
  empty:       { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 160, color: '#64748b', fontSize: 13, gap: 6, textAlign: 'center' },
  pieLegend:   { display: 'flex', flexDirection: 'column', gap: 5, marginTop: 10 },
  legendItem:  { display: 'flex', alignItems: 'center', gap: 7 },
  legendDot:   { width: 9, height: 9, borderRadius: '50%', flexShrink: 0 },
  legendLabel: { color: '#111111', fontSize: 12, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  legendValue: { color: '#64748b', fontSize: 11, fontWeight: 600 },
  domainList:  { display: 'flex', flexDirection: 'column', gap: 10 },
  domainRow:   { display: 'flex', alignItems: 'flex-start', gap: 10 },
  domainRank:  { width: 22, height: 22, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, flexShrink: 0, marginTop: 2 },
  domainInfo:  { flex: 1 },
  domainTop:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 },
  domainName:  { color: '#111111', fontSize: 13, fontWeight: 600 },
  domainPct:   { fontSize: 12, fontWeight: 700 },
  barTrack:    { height: 5, background: '#fee2e2', borderRadius: 3, overflow: 'hidden', marginBottom: 3 },
  barFill:     { height: '100%', borderRadius: 3, transition: 'width 0.4s' },
  domainSub:   { color: '#64748b', fontSize: 11, margin: 0 },
};
